import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { getContent } from "@/content";

const AppContext = createContext(null);

const getInitialLang = () => {
  const params = new URLSearchParams(window.location.search);
  const urlLang = params.get("lang");
  if (urlLang && ["fr", "en", "es"].includes(urlLang)) return urlLang;
  const stored = localStorage.getItem("ifl_lang");
  if (stored && ["fr", "en", "es"].includes(stored)) return stored;
  return "fr";
};

export const AppProvider = ({ children }) => {
  const [lang, setLangState] = useState(getInitialLang);
  const [presenter, setPresenter] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const setLang = useCallback((next) => {
    setLangState(next);
    localStorage.setItem("ifl_lang", next);
    const url = new URL(window.location.href);
    url.searchParams.set("lang", next);
    window.history.replaceState({}, "", url);
  }, []);

  const togglePresenter = useCallback(() => setPresenter((p) => !p), []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  const t = getContent(lang);

  return (
    <AppContext.Provider
      value={{ lang, setLang, t, presenter, togglePresenter, setPresenter, isFullscreen, toggleFullscreen }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
};
